<template>
  <TemplateBase>
    <main class="col-span-full row-start-2">
      <OrganismNavigationBreadcrumb :path="breadcrumbs" class="container mb-4 mx-auto pt-10 px-4 md:mb-8" />
      <div class="mb-8 mx-auto w-full sm:w-8/12">
        <div class="mx-2 pb-8 pt-8 px-6 bg-app-lime rounded-t-xl sm:mx-0 sm:px-12 lg:px-12">
          <h1 class="break-word text-xl font-black leading-none uppercase md:mb-1 md:text-3xl md:font-bold lg:text-5xl">
            {{ page.title }}
          </h1>

          <div v-if="page.postDate || page.expiryDate" class="mb-6 align-top text-sm">
            <time v-if="page.expiryDate" :datetime="new Date(page.expiryDate).toISOString()" class="inline">
              {{ t_('expires-on', { date: $d(new Date(page.expiryDate), 'medium-date') }) }}
            </time>
          </div>
          <div class="grid grid-cols-4 align-bottom items-end text-base md:pt-8">
            <div v-if="page.departmentTitle" class="col-span-full lg:col-span-1 mb-4">
              <p class="font-bold uppercase">{{ page.departmentTitle }}</p>
              <p>Afdeling</p>
            </div>
            <div v-if="page.location" class="col-span-full lg:col-span-1 mb-4">
              <p class="font-bold uppercase">{{ page.location }}</p>
              <p>Locatie</p>
            </div>
            <div v-if="page.jobOfferWorkloadDescription" class="col-span-full lg:col-span-1 mb-4">
              <p class="font-bold uppercase">{{ page.jobOfferWorkloadDescription }}</p>
              <p>Beschikbaarheid</p>
            </div>
            <div class="col-span-full pt-8 lg:mb-4 lg:pt-0 lg:col-span-1">
              <a href="#FormieForm">
                <AtomButton id="lid-mobile-navigation-btn-open-menu" button-type="secondary" class="group cursor-pointer rounded-xl text-white bg-black text-base overflow-hidden">
                  {{ t_('apply') }}
                  <AtomIcon :icon="'arrow-down'" class="w-4 h-4 ml-8" />
                </AtomButton>
              </a>
            </div>
          </div>
        </div>

        <div class="mb-6 mx-2 pb-12 pt-12 px-4 bg-white rounded-b-xl sm:mx-0 sm:px-12 lg:px-12">
          <article class="my-3">
            <h2 class="font-bold uppercase text-lg">{{ t_('for-who') }}</h2>
            <i18n-t :keypath="k_('introduction')" tag="div" class="text-base">
              <template #departmentTitle>
                <strong>{{ page.departmentTitle || page.jobOfferCity }}</strong>
              </template>
              <template #areaProvince>
                <strong>{{ page.jobOfferAreaPreference }}</strong>
              </template>
              <template #city>
                <strong>{{ page.jobOfferCity }}</strong>
              </template>
              <template #title>
                <strong>{{ page.title }}</strong>
              </template>
            </i18n-t>
            <i18n-t v-if="page.jobOfferWorkloadDescription" :keypath="k_('workload-description')" tag="div" class="text-base">
              <template #workloadDescription>
                <strong>{{ page.jobOfferWorkloadDescription }}</strong>
              </template>
            </i18n-t>
          </article>

          <article v-if="page.description" class="prose prose--app mb-6 text-base" v-html="page.description" />

          <article v-if="page.jobOfferTasks && page.jobOfferTasks.length" class="mb-6 text-md">
            <ul>
              <li class="font-bold uppercase text-lg">{{ t_('tasks') }}</li>
              <template v-for="(task, index) in page.jobOfferTasks">
                <li v-if="task" :key="index" class="flex items-center">
                  <AtomIconCheck class="shrink-0 self-start mr-2 mt-2 w-4 h-4 text-app-green" />
                  {{ task }}
                </li>
              </template>
            </ul>
          </article>

          <article v-if="page.jobOfferBenefits && page.jobOfferBenefits.length" class="mb-6 text-md">
            <ul>
              <li class="font-bold uppercase text-lg">{{ t_('benefits') }}</li>
              <li v-if="page.jobOfferType === 'volunteer'">
                {{ t_('benefits-volunteer') }}
              </li>
              <li v-else-if="page.jobOfferType === 'paid'">
                {{ t_('benefits-paid') }}
              </li>
              <template v-for="(benefit, index) in page.jobOfferBenefits">
                <li v-if="benefit" :key="index" class="flex">
                  <AtomIconCheck class="shrink-0 self-start mr-2 mt-2 w-4 h-4 text-app-green" />
                  {{ benefit }}
                </li>
              </template>
            </ul>
          </article>

          <article v-if="page.jobOfferRequirements && page.jobOfferRequirements.length" class="mb-6 text-md">
            <ul>
              <li>
                <h2 class="font-bold uppercase text-lg">{{ t_('requirements') }}</h2>
                {{ t_('requirements-details') }}
              </li>
              <li v-if="page.jobOfferWorkingLevel" class="flex items-center">
                <AtomIconCheck class="shrink-0 self-start mr-2 mt-2 w-4 h-4 text-app-green" />
                {{ t_('requirements-working-level', { workingLevel: page.jobOfferWorkingLevel }) }}
              </li>
              <template v-for="(requirement, index) in page.jobOfferRequirements">
                <li v-if="requirement" :key="`requirement-${index}`" class="flex items-center">
                  <AtomIconCheck class="shrink-0 self-start mr-2 mt-2 w-4 h-4 text-app-green" />
                  {{ requirement }}
                </li>
              </template>
              <template v-if="page.jobOfferCharacteristics && page.jobOfferCharacteristics.length">
                <template v-for="(characteristic, index) in page.jobOfferCharacteristics">
                  <li v-if="characteristic" :key="`characteristic-${index}`" class="flex items-center">
                    <AtomIconCheck class="shrink-0 self-start mr-2 mt-2 w-4 h-4 text-app-green" />
                    {{ characteristic }}
                  </li>
                </template>
              </template>
            </ul>
          </article>

          <div v-if="cta" class="gap-4 rounded-xl grid grid-cols-4 py-3 lg:py-6">
            <div class="col-span-full grid">
              <article class="group relative w-full">
                <AtomLink v-if="cta.button?.url" :url="cta.button?.url" class="flex flex-col lg:flex-row w-full cursor-pointer overflow-hidden items-center">
                  <div class="mission-video-shadow-container w-full lg:w-[unset]">
                    <div class="mission-video-shadow relative">
                      <figure v-if="cta.image" class="rounded-xl overflow-hidden lg:aspect-[16/10] h-60">
                        <MoleculePicture class="relative z-20 v-hover-zoom-image w-full h-1/2 object-cover transform transition duration-200" :image="cta.image" :fallback="true" />
                      </figure>
                    </div>
                  </div>
                  <div class="z-10 lg:px-6 py-6 w-full bg-white rounded-b-xl">
                    <h2>{{}}</h2>
                    <h2 v-if="page.jobOfferType === 'volunteer'" class="font-bold uppercase text-lg">
                      {{ t_('cta-volunteer-title') }}
                    </h2>
                    <h2 v-else-if="page.jobOfferType === 'paid'" class="font-bold uppercase text-lg">
                      {{ t_('cta-paid-title') }}
                    </h2>
                    <p v-if="cta.caption" class="text-base">{{ cta.caption }}</p>
                    <p v-if="cta.button?.label" class="underline underline-offset-4 decoration-app-green font-bold mt-4 text-lg">
                      {{ cta.button?.label }}<AtomIconArrowRight class="inline-block w-3 ml-2 text-app-green" />
                    </p>
                  </div>
                </AtomLink>
              </article>
            </div>
          </div>

          <!--TODO: Change page.department.title (Deprecated) to new department field from AFAS (AfdelingWebsite)-->
          <h2 v-if="page.introduction" class="font-bold uppercase text-lg">{{ t_('about-department', { departmentTitle: 'ons' }) }}</h2>
          <article v-if="page.introduction" class="prose prose--app mb-6 text-base" v-html="page.introduction" />

          <div v-if="page.jobOfferDisplayContact">
            <div class="mt-3 text-lg font-bold">{{ t_('got-questions') }}</div>
            <div class="mb-3 text-lg">{{ t_('contact', { who: page.jobOfferContact }) }}</div>
            <div class="grid grid-cols-3 text-base">
              <div v-if="page.jobOfferContactTelephone" class="col-span-full md:col-span-1">
                <AtomIcon class="inline-block ml-1 w-5 h-5" icon="phone" />
                <a class="font-bold pl-3 underline decoration-app-green" :href="`tel:${page.jobOfferContactTelephone}`">{{ page.jobOfferContactTelephone }}</a>
              </div>
              <div v-if="page.jobOfferContactEmail" class="col-span-full md:col-span-1">
                <AtomIcon class="inline-block ml-1 w-5 h-5" icon="mail" />
                <a class="font-bold pl-3 underline decoration-app-green" :href="`mailto:${page.jobOfferContactEmail}`">{{ page.jobOfferContactEmail }}</a>
              </div>
            </div>
          </div>

          <div class="my-3 text-lg font-bold">{{ t_('cta-apply-now') }}</div>

          <SmoothReflow class="overflow-hidden transition-all duration-500 ease-in-out">
            <AtomButton v-show="!formIsVisible" button-type="primary" :full-width="false" @click.prevent="formIsVisible = !formIsVisible">
              {{ t_('apply') }}
            </AtomButton>
            <FormieForm v-show="formIsVisible" :handle="formHandle" :initial-value="{ __Field_Hidden__afasJobOfferId: page.jobOfferAfasId }" class="mb-12" />
          </SmoothReflow>

          <article v-for="(part, partIndex) of t_('in-closing').split('|')" :key="partIndex" class="my-3 text-base">
            {{ part }}
          </article>

          <article v-if="te_(`in-closing-for-${page.jobOfferType}`)" class="my-3 text-base" v-html="t_(`in-closing-for-${page.jobOfferType}`)" />
        </div>
      </div>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { setupTranslationPrefixer } from '~/composables/util'
import { useCtaStore } from '~/store/call-to-action'

interface Props {
  breadcrumbs: object[]
  page: {
    title: string
    postDate?: string
    expiryDate?: string
    departmentTitle?: string
    location?: string
    jobOfferWorkloadDescription?: string
    jobOfferType: string
    jobOfferTasks?: string[]
    jobOfferBenefits?: string[]
    jobOfferRequirements?: string[]
    jobOfferCharacteristics?: string[]
    jobOfferWorkingLevel?: string
    jobOfferAreaPreference?: string
    jobOfferCity?: string
    description?: string
    introduction?: string
    jobOfferContact?: string
    jobOfferContactTelephone?: string
    jobOfferContactEmail?: string
    department?: string
    jobOfferDisplayContact?: boolean
    jobOfferAfasId: string
  }
}

const props = defineProps<Props>()

const formHandleMap = { paid: 'jobApplicationPaid', internship: 'jobApplicationInternship', volunteer: 'jobApplicationVolunteer' }
const formHandle = formHandleMap[props.page.jobOfferType] || formHandleMap.paid
const formIsVisible = ref(true)
const ctaType =
  props.page.jobOfferType === 'volunteer' ? 'jobOfferVolunteer' : props.page.jobOfferType === 'paid' || props.page.jobOfferType === 'internship' ? 'jobOfferPaid' : null
const ctas = await useCtaStore().fetch(ctaType)

// fetch the first instance of the CTA from the job offer CTA list
const cta = computed(() => {
  const firstCta = ctas?.ctas?.[0]
  if (!firstCta) return null
  return firstCta
})

const { t_, te_, k_ } = setupTranslationPrefixer('JobOfferDetail')
</script>

<style lang="scss" scoped>
.mission-video-shadow-container {
  @apply pb-5 pr-5;

  @screen lg {
    @apply pb-10 pr-10;
  }
}

.mission-video-shadow {
  @apply relative;

  &::before {
    content: ' ';
    top: theme('spacing.5');
    z-index: 0;
    margin-right: -20px;
    @apply w-full bg-app-green-200 rounded-xl absolute h-full right-0;

    @screen lg {
      top: theme('spacing.5');
      margin-right: -20px;
    }
  }
}
</style>
